import React, { useEffect, useState } from 'react';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import { Button, makeStyles, TableHead, TableSortLabel, Tooltip } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import variables from '../../Sys/variable.scss';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StatusChip, { toneForStatus } from '../StatusChip';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    position: 'relative',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.10)',
    overflowX: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  headerCell: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.78rem',
    padding: '13px 16px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderBottom: 'none',
  },
  sortLabel: {
    color: '#fff !important',
    '&:hover': {
      color: 'rgba(255,255,255,0.85) !important',
    },
    '&$sortLabelActive': {
      color: '#fff !important',
    },
  },
  sortLabelActive: {
    color: '#fff !important',
  },
  sortIcon: {
    color: 'rgba(255,255,255,0.6) !important',
    '$sortLabelActive &': {
      color: '#fff !important',
    },
  },
  bodyRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(0,0,0,0.025)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent',
    },
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.055) !important',
    },
    transition: 'background-color 0.15s ease',
  },
  bodyCell: {
    color: variables.darkThemeText,
    padding: '11px 16px',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  loadingCell: {
    padding: '40px 16px',
    textAlign: 'center',
  },
  loadingPlaceholder: {
    padding: '40px 16px',
    borderBottom: 'none',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.65)',
    zIndex: 2,
    pointerEvents: 'none',
  },
  emptyRow: {
    '& td': {
      padding: '36px 16px',
      textAlign: 'center',
      color: variables.darkThemeText,
      fontSize: '0.9rem',
      fontStyle: 'italic',
      borderBottom: 'none',
    },
  },
  editButton: {
    color: '#fff',
    background: '#06AD17',
    boxShadow: '0px 15.8892px 39.723px rgb(191 21 108 / 5%)',
    padding: 8,
    borderRadius: 35,
    '&:hover': {
      background: '#048111',
    },
  },
  /** Disabled IconButton strips background; keep same green as edit for loading spinner */
  editButtonLoading: {
    '&.Mui-disabled': {
      backgroundColor: '#06AD17',
      opacity: 1,
      color: '#fff',
    },
  },
  actionGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 8,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  approveButton: {
    width: 34,
    height: 34,
    padding: '8px !important',
    borderRadius: 8,
    color: '#1f7a3f !important',
    backgroundColor: 'rgba(40, 167, 69, 0.12) !important',
    boxShadow: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(40, 167, 69, 0.2) !important',
    },
  },
  deleteButton: {
    width: 34,
    height: 34,
    padding: '8px !important',
    borderRadius: 8,
    color: '#c62828 !important',
    backgroundColor: 'rgba(245, 25, 25, 0.1) !important',
    boxShadow: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(245, 25, 25, 0.18) !important',
    },
  },
  enrollButton: {
    padding: '7px 22px',
    borderRadius: 5,
  },
  cellColumn: {
    minWidth: 168,
    wordBreak: 'break-all',
  },
  expandButton: {
    color: 'blue',
    cursor: 'pointer',
  },
  tooltipCell: {
    cursor: 'pointer',
  },
  tooltipContent: {
    fontSize: '0.82rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: 360,
  },
}));

function RenderButton({ item, rowItem, handleItemClick, actionLoadingRowId, actionLoadingRowField }) {
  const classes = useStyles();
  const rowId = actionLoadingRowField ? rowItem?.[actionLoadingRowField] : null;
  const isThisRowLoading = actionLoadingRowId != null && rowId != null && actionLoadingRowId === rowId;

  if (item === 'edit') {
    if (isThisRowLoading) {
      return (
        <IconButton
          size="small"
          className={`${classes.editButton} ${classes.editButtonLoading}`}
          disabled
          aria-label="Loading"
        >
          <CircularProgress size={20} thickness={4} style={{ color: '#fff' }} />
        </IconButton>
      );
    }
    return (
      <IconButton size="small" className={classes.editButton} onClick={() => handleItemClick(rowItem, 'edit')}>
        <EditIcon />
      </IconButton>
    );
  }
  if (item === 'enroll') {
    return (
      <Button variant="outlined" className={classes.enrollButton} size="small" color="primary" onClick={() => handleItemClick(rowItem, 'edit')}>
        Enroll
      </Button>
    );
  }
  if (item === 'reject') {
    return (
      <IconButton size="small" className={classes.deleteButton} onClick={() => handleItemClick(rowItem, 'reject')}>
        <CancelIcon />
      </IconButton>
    );
  }
  if (item === 'approve') {
    return (
      <IconButton size="small" className={classes.approveButton} onClick={() => handleItemClick(rowItem, 'approve')}>
        <CheckCircleIcon />
      </IconButton>
    );
  }
  return null;
}

function RenderItem({ rowItem, colItem, handleItemClick, tooltipId, activeTooltipId, setActiveTooltipId, actionLoadingRowId, actionLoadingRowField }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const classes = useStyles();

  const handleToggleText = () => setIsExpanded((prev) => !prev);

  if (typeof colItem.render === 'function') {
    return colItem.render(rowItem);
  }

  const renderLongText = (text) => {
    const words = text.split(' ');
    const truncatedText = words.slice(0, 80).join(' ');
    const fullText = words.join(' ');
    return (
      <div>
        {isExpanded ? fullText : truncatedText}
        {words.length > 80 && (
          <span onClick={handleToggleText} className={classes.expandButton}>
            {isExpanded ? ' less' : ' more'}
          </span>
        )}
      </div>
    );
  };

  if (colItem.tooltip && typeof rowItem[colItem.key] === 'string' && rowItem[colItem.key]) {
    const CHAR_LIMIT = 80;
    const fullText = rowItem[colItem.key];
    const isTruncated = fullText.length > CHAR_LIMIT;
    const displayText = isTruncated ? fullText.slice(0, CHAR_LIMIT) + '…' : fullText;
    const isTouchDevice = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches;
    const isTooltipOpen = activeTooltipId === tooltipId;

    const handleTooltipToggle = () => {
      if (!isTouchDevice || !isTruncated) return;
      if (isTooltipOpen) {
        setActiveTooltipId(null);
      } else {
        setActiveTooltipId(tooltipId);
      }
    };

    return (
      <Tooltip
        title={<span className={classes.tooltipContent}>{fullText}</span>}
        placement="top"
        arrow
        open={isTouchDevice ? isTooltipOpen : undefined}
        onClose={() => setActiveTooltipId(null)}
        disableHoverListener={isTouchDevice}
        disableFocusListener={isTouchDevice}
        disableTouchListener={isTouchDevice}
        enterTouchDelay={0}
        leaveTouchDelay={3000}
      >
        <span
          className={classes.tooltipCell}
          data-tooltip-trigger="true"
          data-tooltip-id={tooltipId}
          onClick={handleTooltipToggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handleTooltipToggle();
          }}
          role="button"
          tabIndex={0}
        >
          {displayText}
        </span>
      </Tooltip>
    );
  }

  if (colItem.key === 'action' && colItem.actions && colItem.actions.length) {
    if (rowItem.status && (rowItem.status === 'declined' || rowItem.status === 'approved')) {
      return <StatusChip tone={toneForStatus(rowItem.status)}>{rowItem.status}</StatusChip>;
    }
    return (
      <div className={classes.actionGroup}>
        {colItem.actions.map((btn) => (
          <RenderButton
            key={btn}
            item={btn}
            handleItemClick={handleItemClick}
            rowItem={rowItem}
            actionLoadingRowId={actionLoadingRowId}
            actionLoadingRowField={actionLoadingRowField}
          />
        ))}
      </div>
    );
  }

  if (colItem.key === 'logo') {
    return rowItem?.logo && rowItem.logo !== '' ? (
      <img src={rowItem.logo} alt="Logo" style={{ marginLeft: 10, width: 'auto', height: '50px', maxWidth: '50px', objectFit: 'contain' }} />
    ) : null;
  }

  if (colItem.key === 'link') {
    return rowItem?.link && rowItem.link !== '' ? (
      <a rel="noreferrer" href={rowItem.link} target="_blank">{rowItem.link}</a>
    ) : null;
  }

  if (typeof rowItem[colItem.key] === 'string' && rowItem[colItem.key].split(' ').length > 80) {
    return renderLongText(rowItem[colItem.key]);
  }

  if (Array.isArray(rowItem[colItem.key])) {
    return rowItem[colItem.key].length ? rowItem[colItem.key].join(', ') : '';
  }

  const value = rowItem[colItem.key];
  if (value != null && value !== 'NA') {
    return <>{value}</>;
  }
  return '';
}

function GenericTable({
  columns,
  list,
  loading,
  handleItemClick,
  left,
  applyMinWidth = true,
  onSort,
  sortBy,
  sortOrder = 'asc',
  actionLoadingRowId = null,
  actionLoadingRowField = null,
}) {
  const classes = useStyles();
  const [activeTooltipId, setActiveTooltipId] = useState(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!activeTooltipId) return;
      const target = event.target;
      if (!target || !target.closest) return;

      const clickedTooltipContent = target.closest('[role="tooltip"]');
      const clickedTrigger = target.closest('[data-tooltip-trigger="true"]');
      const clickedTriggerId = clickedTrigger?.getAttribute('data-tooltip-id');

      if (clickedTooltipContent) return;
      if (clickedTriggerId === activeTooltipId) return;
      setActiveTooltipId(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [activeTooltipId]);

  const handleSortClick = (field) => {
    if (!onSort) return;
    const newDirection = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(field, newDirection);
  };

  const align = left ? 'left' : 'center';

  return (
    <TableContainer className={classes.tableContainer}>
      {loading ? (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      ) : null}
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                key={index}
                align={align}
                className={`${classes.headerCell} ${applyMinWidth ? classes.cellColumn : ''}`}
                style={{
                  ...((col.key === 'name' || col.key === 'firstName' || col.key === 'lastName' || col.key === 'userName' || col.key === 'location' || col.key === 'role') && { minWidth: 100 }),
                  ...((col.key === 'action' || col.key === 'actions') && { minWidth: 140, whiteSpace: 'nowrap' }),
                }}
              >
                {col.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortOrder : 'asc'}
                    onClick={() => handleSortClick(col.key)}
                    classes={{
                      root: classes.sortLabel,
                      active: classes.sortLabelActive,
                      icon: classes.sortIcon,
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className={classes.loadingPlaceholder} align="center" />
            </TableRow>
          ) : list.length > 0 ? (
            list.map((rowItem, rowIndex) => (
              <TableRow key={rowIndex} className={classes.bodyRow}>
                {columns.map((colItem, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={align}
                    className={classes.bodyCell}
                    style={colItem.key === 'action' || colItem.key === 'actions' ? { whiteSpace: 'nowrap' } : undefined}
                  >
                    <RenderItem
                      rowItem={rowItem}
                      colItem={colItem}
                      handleItemClick={handleItemClick}
                      tooltipId={`${rowIndex}-${colItem.key}`}
                      activeTooltipId={activeTooltipId}
                      setActiveTooltipId={setActiveTooltipId}
                      actionLoadingRowId={actionLoadingRowId}
                      actionLoadingRowField={actionLoadingRowField}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className={classes.emptyRow}>
              <TableCell colSpan={columns.length}>No records found.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default GenericTable;
